import { useEffect, useState } from 'react'

import { useTelemetrySource } from '../lib/api/source.tsx'
import { useDebounced } from '../lib/useDebounced.ts'
import { hasInvestigation, logsHref, readInvestigation } from '../lib/logs/investigation.ts'
import { Link, navigate, useQueryParam, useSearchParams } from '../lib/router.tsx'
import type { LogFilter } from '../lib/sse/types.ts'
import { PageHeader, Toolbar } from '../components/layout/PageHeader.tsx'
import { Button } from '../components/primitives/Button.tsx'
import { ShortId } from '../components/primitives/CopyValue.tsx'
import { SearchInput } from '../components/primitives/Field.tsx'
import { AgentPicker, TenantSelect } from '../components/logs/Directory.tsx'
import { ControlCenter } from '../components/logs/ControlCenter.tsx'
import { LogHistory } from '../components/logs/LogHistory.tsx'
import { LogStreamView } from '../components/logs/LogStreamView.tsx'

type Tab = 'live' | 'history'

/**
 * Logs.
 *
 * Duas telas no mesmo componente, e a diferença justifica o desenho inteiro
 * (§2.1): a visão sem filtro é RADAR — uma amostra da frota, e a tela diz isso; a
 * visão por agente é INVESTIGAÇÃO — um agente sozinho fica abaixo do teto do
 * quadro, então nada é amostrado, e só ela precisa de fidelidade total.
 *
 * Sobre as duas: a investigação por pivô (processo, IP, usuário, hash) vive na
 * ABA HISTÓRICO, dirigida pela URL, porque o tail ao vivo não sustenta esses
 * filtros e porque o recorte precisa ser colável num ticket (§7). Chegar por um
 * pivô abre o histórico já filtrado.
 */
export function Logs({ endpointId }: { endpointId?: string }) {
  return endpointId === undefined ? <FleetLogs /> : <AgentLogs endpointId={endpointId} />
}

/** Abas ao vivo / histórico. */
function Tabs({ tab, onChange }: { tab: Tab; onChange: (tab: Tab) => void }) {
  return (
    <div className="flex items-center gap-1" role="tablist" aria-label="Modo de consulta">
      {(['live', 'history'] as const).map((value) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={tab === value}
          onClick={() => onChange(value)}
          className={`border-b-2 px-2 py-1 text-sm ${
            tab === value
              ? 'border-accent text-fg'
              : 'border-transparent text-fg-muted hover:text-fg'
          }`}
        >
          {value === 'live' ? 'Ao vivo' : 'Histórico'}
        </button>
      ))}
    </div>
  )
}

function FleetLogs() {
  const source = useTelemetrySource()
  const read = readInvestigation(useSearchParams())
  // Cliente: nenhum escopo de empresa na URL — a empresa vem do token.
  const inv = source.multiTenant ? read : { ...read, tenantId: undefined }
  const investigating = hasInvestigation(inv)

  // Radar ao vivo: filtro de tipo LOCAL e debounced. Não vai para a URL — é
  // sintonia efêmera do tail, não uma investigação compartilhável. Cada mudança
  // reabre a conexão, e cada reabertura é uma linha no log de auditoria (§0.4).
  const [eventTypeInput, setEventTypeInput] = useState('')
  const eventType = useDebounced(eventTypeInput)

  const [tab, setTab] = useState<Tab>(() => (investigating ? 'history' : 'live'))
  // Um pivô clicado troca a URL sem desmontar a tela: force o histórico.
  useEffect(() => {
    if (investigating) setTab('history')
  }, [investigating])

  const tenantId = inv.tenantId ?? ''
  const setTenant = (value: string): void => navigate(logsHref({ ...inv, tenantId: value || undefined }))

  const filter: LogFilter = {
    tenantId: tenantId || undefined,
    eventType: eventType || undefined,
  }

  return (
    <>
      <PageHeader
        title="Logs"
        actions={<Tabs tab={tab} onChange={setTab} />}
      />

      <Toolbar>
        {source.multiTenant && <TenantSelect value={tenantId} onChange={setTenant} />}
        {tab === 'live' && (
          <SearchInput
            label="Filtrar o tail por tipo"
            placeholder="tipo de evento"
            value={eventTypeInput}
            onValueChange={setEventTypeInput}
            className="w-48"
          />
        )}
        <div className="ml-auto min-w-0">
          <AgentPicker
            tenantId={tenantId || undefined}
            onPick={(item) => navigate(`/logs/${item.endpoint_id}`)}
          />
        </div>
      </Toolbar>

      {tab === 'live' ? (
        <LogStreamView filter={filter} onOpenHistory={() => setTab('history')} />
      ) : (
        <LogHistory investigation={inv} />
      )}
    </>
  )
}

function AgentLogs({ endpointId }: { endpointId: string }) {
  const telemetry = useTelemetrySource()
  const read = readInvestigation(useSearchParams())
  const inv = telemetry.multiTenant ? read : { ...read, tenantId: undefined }
  const investigating = hasInvestigation(inv)

  // Radar ao vivo do agente: tipo e canal LOCAIS e debounced (ver FleetLogs).
  const [eventTypeInput, setEventTypeInput] = useState('')
  const [sourceInput, setSourceInput] = useState('')
  const eventType = useDebounced(eventTypeInput)
  const source = useDebounced(sourceInput)

  const [tab, setTab] = useState<Tab>(() => (investigating ? 'history' : 'live'))
  useEffect(() => {
    if (investigating) setTab('history')
  }, [investigating])

  // ?comando=<id> abre o centro de controle direto no comando aberto (§6.5).
  const comando = useQueryParam('comando')
  const [controlOpen, setControlOpen] = useState(false)
  // Comandos são da Black Ghost: o cliente não tem centro de controle.
  const canCommand = telemetry.multiTenant
  useEffect(() => {
    if (comando !== null && canCommand) setControlOpen(true)
  }, [comando, canCommand])

  const filter: LogFilter = {
    endpointId,
    eventType: eventType || undefined,
    source: source || undefined,
  }

  return (
    <>
      <PageHeader
        title="Logs do agente"
        actions={
          <>
            <Tabs tab={tab} onChange={setTab} />
            {canCommand && (
              <Button variant="primary" onClick={() => setControlOpen(true)}>
                Centro de Controle
              </Button>
            )}
          </>
        }
      />

      <Toolbar>
        <Link href="/logs" className="text-sm text-fg-muted underline">
          ← todos os agentes
        </Link>
        <span className="text-micro text-fg-muted">endpoint_id</span>
        <ShortId id={endpointId} label="endpoint_id" />
        {tab === 'live' && (
          <div className="ml-auto flex items-center gap-2">
            <SearchInput
              label="Filtrar o tail por tipo"
              placeholder="tipo de evento"
              value={eventTypeInput}
              onValueChange={setEventTypeInput}
              className="w-44"
            />
            <SearchInput
              label="Filtrar o tail por canal"
              placeholder="canal (source)"
              value={sourceInput}
              onValueChange={setSourceInput}
              className="w-44"
            />
          </div>
        )}
      </Toolbar>

      {tab === 'live' ? (
        <LogStreamView filter={filter} onOpenHistory={() => setTab('history')} />
      ) : (
        <LogHistory scopeEndpointId={endpointId} investigation={inv} />
      )}

      {canCommand && controlOpen && (
        <ControlCenter endpointId={endpointId} onClose={() => setControlOpen(false)} />
      )}
    </>
  )
}
